import React, { useCallback, useEffect } from 'react';
import noop from 'lodash/noop';
import { ClientSideValidationError } from '@atlassian/jira-business-error-handling/src/utils/client-side-validation-error/index.tsx';
import { UnsupportedOperationError } from '@atlassian/jira-business-error-handling/src/utils/unsupported-operation-error/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	VALIDATION_ERROR_CODES,
	SUPPORTED_TOP_LEVEL_FIELD_TYPES,
} from '../../common/clipboard-handler/constants.tsx';
import { getClipboardHandlerComponent } from '../../common/clipboard-handler/index.tsx';
import { getCellValue } from '../../common/clipboard-handler/utils.tsx';
import type {
	ListIssueFieldTransformedUnionForCopyPaste,
	ClipboardItem,
	ListIssueForCopyPaste,
} from '../../common/types.tsx';
import { getFieldMetadataFromClipboard } from './utils.tsx';

const PrepareToCopyComponent = ({
	issue,
	field,
	onMissingClipboardHandler,
	onContentReady,
}: {
	issue: ListIssueForCopyPaste;
	field: ListIssueFieldTransformedUnionForCopyPaste;
	onMissingClipboardHandler: () => ClipboardItem | null;
	onContentReady: (clipboardItem: ClipboardItem | null) => void;
}) => {
	const Component = getClipboardHandlerComponent(field?.type);

	useEffect(() => {
		if (!Component) {
			try {
				const result = onMissingClipboardHandler();
				onContentReady(result);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				onContentReady(null);
			}
		}
	}, [Component, onContentReady, onMissingClipboardHandler]);

	if (!Component) {
		return null;
	}

	return (
		<Component
			issue={issue}
			field={field}
			action="prepare-to-copy"
			onComplete={noop}
			onContentReady={onContentReady}
			metadata={null}
		/>
	);
};

const CutOnlyHandlerComponent = ({
	issue,
	field,
	onCut,
	isEditable,
}: {
	issue: ListIssueForCopyPaste;
	field: ListIssueFieldTransformedUnionForCopyPaste;
	onCut: (success: boolean, error?: Error) => void;
	isEditable: boolean;
}) => {
	const Component = getClipboardHandlerComponent(field.type);

	const checkIsEditable = useCallback(() => {
		if (!isEditable) {
			throw new ClientSideValidationError(VALIDATION_ERROR_CODES.NON_EDITABLE);
		}
	}, [isEditable]);

	if (!isEditable) {
		onCut(false, new ClientSideValidationError(VALIDATION_ERROR_CODES.NON_EDITABLE));
		return null;
	}

	if (!Component) {
		return null;
	}

	return (
		<Component
			issue={issue}
			field={field}
			action="cut-only"
			onComplete={onCut}
			metadata={{ fieldId: field.fieldId, fieldType: field.type, fieldValue: null }}
			onBeforeAction={checkIsEditable}
		/>
	);
};

export const useClipboard = (
	issue?: ListIssueForCopyPaste,
	field?: ListIssueFieldTransformedUnionForCopyPaste,
	fieldId?: string,
) => {
	const { formatMessage } = useIntl();

	const getCellValueFallback = useCallback(() => {
		if (!field) {
			throw new ClientSideValidationError(VALIDATION_ERROR_CODES.MISSING_FIELD);
		}

		return getCellValue(field, issue, formatMessage);
	}, [field, formatMessage, issue]);

	const renderPrepareToCopyComponent = ({
		onContentReady,
	}: {
		onContentReady: (clipboardItem: ClipboardItem | null) => void;
	}) => {
		if (!field || !issue?.key || !issue?.issueId) {
			return;
		}

		return (
			<PrepareToCopyComponent
				issue={issue}
				field={field}
				onMissingClipboardHandler={getCellValueFallback}
				onContentReady={onContentReady}
			/>
		);
	};

	const renderPasteHandlerComponent = ({
		clipboard,
		onPaste,
		isEditable,
		onBeforePaste,
	}: {
		clipboard: ClipboardItem;
		isEditable: boolean;
		onPaste: (success: boolean, error?: Error) => void;
		onBeforePaste?: () => void;
	}) => {
		if (!issue?.key || !issue?.issueId) {
			return onPaste(false, new ClientSideValidationError(VALIDATION_ERROR_CODES.MISSING_ISSUE));
		}

		const isTopLevelField = fieldId && SUPPORTED_TOP_LEVEL_FIELD_TYPES.includes(fieldId);
		const fieldType = isTopLevelField ? fieldId : field?.type;

		if (!fieldType) {
			return onPaste(false, new ClientSideValidationError(VALIDATION_ERROR_CODES.MISSING_FIELD));
		}

		const PasteHandlerComponent = field && getClipboardHandlerComponent(field?.type);
		if (!PasteHandlerComponent || !isEditable) {
			return onPaste(
				false,
				new UnsupportedOperationError(`Pasting on field type ${fieldType} is not supported`),
			);
		}

		if (!clipboard?.plainText) {
			return onPaste(false, new ClientSideValidationError(VALIDATION_ERROR_CODES.EMPTY_CLIPBOARD));
		}

		const metadata = getFieldMetadataFromClipboard(clipboard);

		return (
			<PasteHandlerComponent
				issue={issue}
				field={field}
				action="paste"
				clipboardItem={clipboard}
				onBeforeAction={onBeforePaste}
				onComplete={onPaste}
				metadata={metadata}
			/>
		);
	};

	const renderCutOnlyHandlerComponent = ({
		onCut,
		isEditable,
	}: {
		onCut: (success: boolean, error?: Error) => void;
		isEditable: boolean;
	}) => {
		if (!field || !issue?.key || !issue?.issueId) {
			return onCut(false, new ClientSideValidationError(VALIDATION_ERROR_CODES.MISSING_FIELD));
		}

		return (
			<CutOnlyHandlerComponent issue={issue} field={field} onCut={onCut} isEditable={isEditable} />
		);
	};

	if (!fg('jira_list_keyboard_nav')) {
		return {
			renderPasteHandlerComponent: () => <></>,
			renderPrepareToCopyComponent: () => <></>,
			renderCutOnlyHandlerComponent: () => <></>,
		};
	}

	return {
		renderPasteHandlerComponent,
		renderPrepareToCopyComponent,
		renderCutOnlyHandlerComponent,
	};
};
